import {
  DonorInfoRequestChannel,
  donorInfoRequestChannelLabel,
} from '@signalvote/shared';

/**
 * "How did you ask?" — the one channel picker, shared by the triage missing-info panel and
 * the contact follow-up callout so the two surfaces can't drift on which channels exist or
 * what they're called.
 *
 * There is deliberately no default selection. A pre-selected channel would be a fact the
 * treasurer never asserted, and the obligations that read this distinguish a written request
 * from an oral one — so the caller keeps its action disabled until `value` is non-null.
 *
 * The options come straight off the enum rather than a curated list: a channel the system can
 * store is a channel the treasurer can pick, and keeping a second copy of the members only
 * created something that had to be proved identical to the enum.
 */
export function DonorInfoChannelPicker({
  value,
  onChange,
  disabled,
  id,
  className,
}: {
  value: DonorInfoRequestChannel | null;
  onChange: (channel: DonorInfoRequestChannel) => void;
  disabled?: boolean;
  /**
   * Identifies this picker: it names the radio group (so two mounted pickers don't share a
   * selection) and prefixes each option's test id.
   */
  id: string;
  /** Layout + text colour for the group; the label and options inherit the colour. */
  className?: string;
}) {
  return (
    <fieldset className={className}>
      <legend className="sr-only">How did you ask the donor?</legend>
      <span>How did you ask?</span>
      {Object.values(DonorInfoRequestChannel).map((option) => (
        <label key={option} className="flex cursor-pointer items-center gap-1">
          <input
            type="radio"
            name={id}
            value={option}
            data-testid={`${id}-${option}`}
            checked={value === option}
            disabled={disabled}
            onChange={() => onChange(option)}
            className="h-3 w-3"
          />
          {donorInfoRequestChannelLabel(option)}
        </label>
      ))}
    </fieldset>
  );
}
